import { useEffect, createContext, useContext, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import Intercom from '@intercom/messenger-js-sdk';

const INTERCOM_APP_ID = 'idmocnit';

const IntercomContext = createContext({
  setActiveTab: () => {},
});

export const useIntercom = () => useContext(IntercomContext);

const IntercomProvider = ({ children }) => {
  const { isAuthenticated, user, isLoading } = useAuth0();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isInitialized, setIsInitialized] = useState(false);

  // Determine if Intercom should be visible based on route and active tab
  const shouldShowIntercom = useCallback(() => {
    const path = location.pathname;

    // Show on landing page
    if (path === '/') {
      return true;
    }

    // Show on /app only for dashboard and membership tabs
    if (path === '/app') {
      return activeTab === 'dashboard' || activeTab === 'membership';
    }

    // Hide on all other pages
    return false;
  }, [location.pathname, activeTab]);

  // Initialize Intercom once
  useEffect(() => {
    if (isLoading || isInitialized) return;

    if (isAuthenticated && user) {
      Intercom({
        app_id: INTERCOM_APP_ID,
        user_id: user.sub,
        name: user.name || user.nickname || user.email?.split('@')[0],
        email: user.email,
        created_at: user.updated_at ? Math.floor(new Date(user.updated_at).getTime() / 1000) : undefined,
        hide_default_launcher: true, // We'll control visibility manually
      });
    } else {
      Intercom({
        app_id: INTERCOM_APP_ID,
        hide_default_launcher: true,
      });
    }
    setIsInitialized(true);

    return () => {
      if (window.Intercom) {
        window.Intercom('shutdown');
      }
    };
  }, [isAuthenticated, user, isLoading, isInitialized]);

  // Control visibility based on route and active tab
  useEffect(() => {
    if (!isInitialized || !window.Intercom) return;

    if (shouldShowIntercom()) {
      window.Intercom('update', { hide_default_launcher: false });
    } else {
      window.Intercom('update', { hide_default_launcher: true });
    }
  }, [shouldShowIntercom, isInitialized, location.pathname, activeTab]);

  return (
    <IntercomContext.Provider value={{ setActiveTab }}>
      {children}
    </IntercomContext.Provider>
  );
};

export default IntercomProvider;
